<template>
    <div class="container" v-if="!displayModalChoixSite">
        <div class="title-container">
            <span>
                {{ $t('preventive.page.title') }}
                <ReglePastille cle="interventionPreventive.definition" />
            </span>
        </div>
        <div class="content-container">
            <div class="filter-container">
                <div class="filter-site-container" v-if="!isUniqueSite">
                    {{$t('intervention.non-preventive.filter')}}
                    <ReglePastille cle="suiviInterventionsPreventives.filtreSiteUnique" />
                    <ButtonBlue
                        :on-click="selectionnerSite"
                        :text="$t('bouton.choix.site')"
                        class="button-confirm btn"
                        :disabled="tableData.length === 0 && isLoading || filtreEquipementLoading || filtreGroupeLoading"
                    />
                </div>
                <Filter
                    @search="search"
                    @setInterventionId="this.setInterventionId"
                    @setPredictedDateFrom="this.setPredictedDateFrom"
                    @setPredictedDateTo="this.setPredictedDateTo"
                    @addFilterStatus="this.addFilterStatus"
                    @addFilterGroupe="this.addFilterGroupe"
                    @removeFilterGroupe="this.removeFilterGroupe"
                    @addFilterSousGroupe="this.addFilterSousGroupe"
                    @removeFilterSousGroupe="this.removeFilterSousGroupe"
                    @addFilterEquipement="this.addFilterEquipement"
                    @removeFilterEquipement="this.removeFilterEquipement"
                    @removeFilterStatus="this.removeFilterStatus"
                    @clearFilter="this.clearFilter"
                    @statusToggleSelectAll="this.statusToggleSelectAll"
                    @groupeToggleSelectAll="this.groupeToggleSelectAll"
                    @sousGroupeToggleSelectAll="this.sousGroupeToggleSelectAll"
                    @equipementToggleSelectAll="this.equipementToggleSelectAll"
                    :filter="this.filter"
                    :siteList="this.siteList"
                    :sousGroupeList="this.sousGroupeList"
                    :groupeList="this.groupeList"
                    :equipementList="this.equipementListFiltered"
                    :statusList="this.statusList"
                    class="filter"
                />
            </div>

            <div class="dataTable">
                <div>
                    <h2>
                        {{ $tc('preventive.list.title', + this.count) }}
                        <ReglePastille cle="suiviInterventionsPreventives.portee" />
                        <ReglePastille cle="suiviInterventionsPreventives.filtresEnCascade" />
                    </h2>
                    <BorderedIconButton v-if="!isExportDisabled" @click="exportXls" :imgUrl="iconeXls" :title="$t('list.export')"  />
                    <ReglePastille cle="suiviInterventionsPreventives.exportLimiteEquipements" />
                </div>
                <LoaderComponent v-if="tableData.length === 0 && isLoading" class="loader" />
                <div v-else ref="content" @scroll="handleScroll" class="datatable-container-smaller">
                    <DataTable
                        :count="+count"
                        :isLoading="isLoading"
                        :tableData="this.tableData"
                        :tableHeader="this.tableHeader"
                        :stop-propa-on-pdf="true"
                        @downloadPdf="downloadPdf"
                        @sort="doSort"
                        @select="goToPage"
                    />
                </div>
            </div>
            <WaitingModal v-if="isExportXlsLoading"/>
        </div>
    </div>
    <ModalChoixSite v-else
                    @cancel="modalChoixSiteCancel"
                    @valider="validerSite"
                    :selected-site="null"
                    :selected-sites="filterSite"
                    :is-multi-site="true"
                    :inclure-termines="true"/>
</template>

<script lang="ts">
import DataTable from '@/components/ui-components/DataTable.vue';
import { statusName } from '@/components/ui-components/StatusTag.vue';
import { SortType, sortValue } from '@/models/SortType';
import { defineComponent, onMounted, ref } from 'vue';
import { TableHeader } from '@/models/TableHeader';
import LoaderComponent from '@/components/ui-components/LoaderComponent.vue';
import { store } from '@/plugins/store';
import WaitingModal from '@/components/ui-components/WaitingModal.vue';
import router from '@/router';
import { SUIVI_INTERVENTION_PREVENTIVE } from '@/modules/SuiviInterventionsPreventives/store/mutation-types';
import { SuiviInterventionPreventiveActions } from '@/modules/SuiviInterventionsPreventives/store/action-types';
import { InterventionPreventive } from '@/models/InterventionPreventive';
import DownloadPdf from '@/common/utils/DownloadPdf';
import Filter from '@/modules/Common/components/Filter.vue';
import ButtonBlue from '@/components/ui-components/ButtonBlue.vue';
import ModalChoixSite from '@/modules/Common/components/ModalChoixSite.vue';
import { Site } from '@/models/Site';
import { EtatsTravaux } from '@/models/InterventionNonPreventive';
import { Groupe } from '@/models/Groupe';
import { SousGroupe } from '@/models/SousGroupe';
import { EquipementFilter } from '@/models/EquipementFilter';
import SiteListService, { SiteSearchDTO } from '@/modules/SiteList/service/SiteListService';
import EquipementService from '@/modules/Equipements/services/EquipementService';
import BorderedIconButton from '@/components/ui-components/BorderedIconButton.vue';
import StringUtils from '@/common/utils/StringUtils';
import ErrorManager from '@/common/utils/ErrorManager';
import { ERROR } from '@/modules/ErrorToaster/store/mutation-types';
import ReglePastille from '@/components/regles/ReglePastille.vue';

interface Data {
    debounce: number;
    searchSite: string;
    statusList: string[];
    siteList: any[];
    filter: {
        siteList: any[];
        statusList: string[];
        interventionId: string;
        predictedDateFrom: string;
        predictedDateTo: string;
        groupeList: any[],
        sousGroupeList: any[],
        equipementList: any[],
    };
    siteListTotal: any[],
    displayModalChoixSite: boolean,
    isUniqueSite: boolean;
    groupeList: any[],
    sousGroupeList: any[],
    equipementList: any[],
    equipementListFiltered: any[],
    filtreGroupeLoading: boolean,
    filtreEquipementLoading: boolean,
}

export default defineComponent({
    name: 'ListInterventionPreventivePage',
    components: { ModalChoixSite, ButtonBlue, Filter, DataTable, LoaderComponent, WaitingModal, BorderedIconButton, ReglePastille },
    data(): Data {
        return {
            debounce: 0,
            searchSite: '',
            statusList: [],
            siteList: [],
            filter: {
                siteList: [],
                statusList: [],
                groupeList: [],
                sousGroupeList: [],
                equipementList: [],
                interventionId: '',
                predictedDateFrom: '',
                predictedDateTo: '',
            },
            siteListTotal: [],
            displayModalChoixSite: false,
            isUniqueSite: false,
            groupeList: [],
            sousGroupeList: [],
            equipementList: [],
            equipementListFiltered: [],
            filtreGroupeLoading: false,
            filtreEquipementLoading: false,
        };
    },
    setup() {
        const content = ref<Element>();

        onMounted(() => {
            content.value;
        });

        return { content };
    },
    async beforeMount() {
        this.filter.siteList = this.filterSite;
        await SiteListService.getSiteList(new SiteSearchDTO(0, '', true, true)).then(async res => {
            if (res.data.length === 1) {
                this.isUniqueSite = true;
                this.displayModalChoixSite = false;
            }
            this.siteList = res.data.map(
                ({ id, chantierId, description }: { id: string; chantierId: number; description: string }) => ({
                    id,
                    chantierId,
                    description,
                })
            );

            this.siteListTotal = this.siteList;
            if (this.filterSite == null || this.filterSite.length === 0) {
                store.commit(`suiviInterventionPreventiveModuleState/${SUIVI_INTERVENTION_PREVENTIVE.SET_FILTER_SITE}`, this.siteListTotal.map((site => site.id)));
            }
            this.getEquipementList();
            this.getListGroupBySite();
        });
        this.statusList = Object.values(EtatsTravaux);
    },
    mounted() {
        this.content?.scrollTo({ top: this.scroll });
        this.filter.predictedDateFrom = this.filterPredictedDateFrom;
        this.filter.predictedDateTo = this.filterPredictedDateTo;
        this.filter.statusList = this.filterStatus;
        this.$store.dispatch(`suiviInterventionPreventiveModuleState/${SuiviInterventionPreventiveActions.NAV_FILTER}`,
            {etats: [store.getters['suiviInterventionPreventiveModuleState/getSuiviInterventionFilterStatus']],
                dateDebutPeriodeRechercheParDate: store.getters['suiviInterventionPreventiveModuleState/getSuiviInterventionFilterPredictedDateFrom'],
                dateFinPeriodeRechercheParDate: store.getters['suiviInterventionPreventiveModuleState/getSuiviInterventionFilterPredictedDateTo'],
                siteIds: this.filterSite?.length === this.siteListTotal?.length ? [] : this.filterSite.map(site => site.id),},
            {root: true});
    },
    methods: {
        getListGroupBySite(){
            this.filtreGroupeLoading = true;
            const adressesSiteIds: number[] = this.filterSite.length !== this.siteListTotal.length ? this.filterSite.map(site => site.id) : [];
            SiteListService.getGroupeListBySites(adressesSiteIds).then(response => {
                this.groupeList = response.data.map(
                    ({id, libelle, sousGroupes}: {id: number; libelle: string, sousGroupes: SousGroupe[]}) => ({
                        id,
                        libelle,
                        sousGroupes
                    }));
                this.filter.groupeList = this.groupeList.length > 0 ? this.groupeList  : [];
                this.$store.commit(`suiviInterventionPreventiveModuleState/${SUIVI_INTERVENTION_PREVENTIVE.SET_FILTER_GROUPE}`, this.filter.groupeList);

                this.sousGroupeList = [];
                response.data.forEach((groupe: Groupe) => {
                    if (groupe.sousGroupes) {
                        groupe.sousGroupes.forEach((sousGroupe: SousGroupe) => {
                            this.sousGroupeList.push(sousGroupe);
                        });
                    }
                });
                this.filter.sousGroupeList = this.sousGroupeList.length > 0 ? this.sousGroupeList  : [];
                this.filtreGroupeLoading = false;
            });
            this.$store.commit(`suiviInterventionPreventiveModuleState/${SUIVI_INTERVENTION_PREVENTIVE.SET_FILTER_SOUS_GROUPE}`, this.filter.sousGroupeList);
        },
        getEquipementList() {
            this.filtreEquipementLoading = true;
            const adressesSiteIds: number[] = this.filterSite.length !== this.siteListTotal.length ? this.filterSite.map(site => site.id) : [];

            EquipementService.getEquipementBySites(adressesSiteIds, '', '').then(async response => {
                this.equipementList = response.data.map(({id, libelle, phaseLibelle, groupeId, sousGroupeId}: {id: number; libelle: string, phaseLibelle: string, groupeId: number, sousGroupeId: number}) => ({
                    id,
                    libelle,
                    phaseLibelle,
                    groupeId,
                    sousGroupeId
                }));
                this.equipementListFiltered = this.equipementList;

                this.filter.equipementList = this.equipementListFiltered.length > 0 ? this.equipementListFiltered : [];
                this.$store.commit(`suiviInterventionPreventiveModuleState/${SUIVI_INTERVENTION_PREVENTIVE.SET_FILTER_EQUIPEMENT}`, this.filter.equipementList);
                this.filtreEquipementLoading = false;
            });

        },
        getInterventionData(page = 0) {
            page === 0 && this.content?.scrollTo({ top: 0 });
            store.dispatch(`suiviInterventionPreventiveModuleState/${SuiviInterventionPreventiveActions.GET_SUIVIS_INTERVENTION}`, {
                page,
                sort: this.sort && sortValue(this.sort.sortData, this.sort.sortStatus),
                etats: this.filterStatus,
                adresseSiteIds: this.filterSite?.length === this.siteListTotal?.length ? [] : this.filterSite.map(site => site.id),
                interventionId: this.filterInterventionId,
                equipementIds: this.equipementListFiltered?.length === this.filter.equipementList?.length ? [] : this.filter.equipementList.map(e => e.id),
                groupeIds: (this.filterGroupe?.length === this.groupeList?.length || this.filterGroupe.length > 500) ? [] : this.filterGroupe?.map(x => x.id),
                sousGroupeIds: (this.filterSousGroupe?.length === this.sousGroupeList?.length || this.filterSousGroupe.length > 500) ? [] : this.filterSousGroupe?.map(x => x.id),
                dateDebutPeriodeRechercheParDate:
                    this.filterPredictedDateFrom !== '' ? this.filterPredictedDateFrom : undefined,
                dateFinPeriodeRechercheParDate:
                    this.filterPredictedDateTo !== '' ? this.filterPredictedDateTo : undefined,
            });
        },
        doSort(sort: { sortData: string; sortStatus: SortType }) {
            const { sortData, sortStatus } = sort;
            store.commit(`suiviInterventionPreventiveModuleState/${SUIVI_INTERVENTION_PREVENTIVE.SET_SORT}`, { sortData, sortStatus });
            store.commit(`suiviInterventionPreventiveModuleState/${SUIVI_INTERVENTION_PREVENTIVE.RESET_ITEMS}`);
            this.getInterventionData();
        },
        handleScroll() {
            const top = this.content?.scrollTop ?? 0;
            const max = this.content?.scrollHeight ?? 0;
            const height = this.content?.clientHeight ?? 0;
            store.commit(`suiviInterventionPreventiveModuleState/${SUIVI_INTERVENTION_PREVENTIVE.SET_SCROLL}`, top);
            if (max - top <= height * 1.5 && !this.isLoading && this.page + 1 < this.numberOfPages) {
                this.getInterventionData(this.page + 1);
            }
        },
        goToPage(item: { id: { pageName: string; value: number; }; }){
            router.push({ name: item.id.pageName, params: { id: item.id.value } });
        },
        async downloadPdf(item: any) {
            DownloadPdf.getInterventionPdf(item.id.value, 'Rapport_intervention_préventive-');
        },
        search() {
            store.commit(`suiviInterventionPreventiveModuleState/${SUIVI_INTERVENTION_PREVENTIVE.RESET_ITEMS}`);
            this.doFilter();
        },
        doFilter() {
            store.commit(`suiviInterventionPreventiveModuleState/${SUIVI_INTERVENTION_PREVENTIVE.SET_FILTER_SITE}`, this.filter.siteList);
            store.commit(`suiviInterventionPreventiveModuleState/${SUIVI_INTERVENTION_PREVENTIVE.SET_FILTER_STATUS}`, this.filter.statusList);
            store.commit(`suiviInterventionPreventiveModuleState/${SUIVI_INTERVENTION_PREVENTIVE.SET_FILTER_INTERVENTION_ID}`, this.filter.interventionId);
            store.commit(`suiviInterventionPreventiveModuleState/${SUIVI_INTERVENTION_PREVENTIVE.SET_FILTER_PREDICTED_DATE_FROM}`, this.filter.predictedDateFrom);
            store.commit(`suiviInterventionPreventiveModuleState/${SUIVI_INTERVENTION_PREVENTIVE.SET_FILTER_PREDICTED_DATE_TO}`, this.filter.predictedDateTo);
            store.commit(`suiviInterventionPreventiveModuleState/${SUIVI_INTERVENTION_PREVENTIVE.SET_FILTER_GROUPE}`, this.filter.groupeList);
            store.commit(`suiviInterventionPreventiveModuleState/${SUIVI_INTERVENTION_PREVENTIVE.SET_FILTER_SOUS_GROUPE}`, this.filter.sousGroupeList);
            this.getInterventionData();
        },
        setInterventionId(value: string): void {
            this.filter.interventionId = value;
        },
        setPredictedDateFrom(value: string): void {
            this.filter.predictedDateFrom = value;
        },
        setPredictedDateTo(value: string): void {
            this.filter.predictedDateTo = value;
        },
        addFilterStatus(status: string) {
            this.filter.statusList.push(status);
        },
        removeFilterStatus(status: string) {
            this.filter.statusList = this.filter.statusList.filter(statusData => statusData !== status);
        },
        statusToggleSelectAll() {
            this.filter.statusList = this.filter.statusList.length === this.statusList.length ? [] : this.statusList;
        },
        clearFilter() {
            this.filter.siteList = [];
            this.filter.statusList = this.statusList ?? [];
            this.filter.interventionId = '';
            this.filter.predictedDateFrom = '';
            this.filter.predictedDateTo = '';

            this.doFilter();
        },
        validerSite(sites: Site[]) : void {
            this.siteList = sites;
            this.displayModalChoixSite = false;
            store.commit(`suiviInterventionPreventiveModuleState/${SUIVI_INTERVENTION_PREVENTIVE.SET_FILTER_SITE}`, sites);
            store.commit(`suiviInterventionPreventiveModuleState/${SUIVI_INTERVENTION_PREVENTIVE.RESET_ITEMS}`);
            this.getListGroupBySite();
            this.getInterventionData();
            this.getEquipementList();
        },
        modalChoixSiteCancel(): void {
            this.siteList = this.siteListTotal;
            this.displayModalChoixSite = false;
            if (this.tableData.length === 0 && !this.isLoading) this.getInterventionData();
        },
        selectionnerSite() : void {
            this.displayModalChoixSite = true;
        },
        addFilterGroupe(groupe: Groupe) {
            this.filter.groupeList?.push(groupe);
            this.refreshSousGroupe();
            this.refreshEquipement();
        },
        removeFilterGroupe(groupe: Groupe) {
            this.filter.groupeList = this.filter.groupeList?.filter(groupeData => groupeData !== groupe);
            this.refreshSousGroupe();
            this.refreshEquipement();
        },
        addFilterSousGroupe(sousGroupe: SousGroupe) {
            this.filter.sousGroupeList?.push(sousGroupe);
            this.refreshEquipementBySousGroupe();
        },
        removeFilterSousGroupe(sousGroupe: SousGroupe) {
            this.filter.sousGroupeList = this.filter.sousGroupeList?.filter(sousGroupeData => sousGroupeData !== sousGroupe);
            this.refreshEquipementBySousGroupe();
        },
        addFilterEquipement(equipement: EquipementFilter) {
            this.filter.equipementList.push(equipement);
        },
        removeFilterEquipement(equipement: EquipementFilter) {
            this.filter.equipementList = this.filter.equipementList.filter(equipementItem => equipementItem !== equipement);
        },
        equipementToggleSelectAll() {
            this.filter.equipementList = this.filter.equipementList.length === this.equipementListFiltered.length ? [] : this.equipementListFiltered;
        },
        groupeToggleSelectAll() {
            this.filter.groupeList = this.filter.groupeList.length === this.groupeList.length ? [] : this.groupeList;
            this.refreshSousGroupe();
            this.refreshEquipement();
        },
        sousGroupeToggleSelectAll() {
            this.filter.sousGroupeList = this.filter.sousGroupeList.length === this.sousGroupeList.length ? [] : this.sousGroupeList;
            this.refreshEquipement();
        },
        refreshSousGroupe() {
            this.sousGroupeList = [];
            this.filter.groupeList.forEach(groupe => {
                if (groupe.sousGroupes != null) {
                    groupe.sousGroupes.forEach((sousGroupe: SousGroupe) => this.sousGroupeList.push(sousGroupe));
                }
            });
            this.filter.sousGroupeList = this.sousGroupeList;
            this.sousGroupeList.sort();
        },
        refreshEquipement() {
            this.equipementListFiltered = [];
            this.filter.groupeList.forEach(groupe => {
                const equipements = this.equipementList.filter(equipement => equipement.groupeId === groupe.id);
                if (equipements != null) {
                    equipements.forEach(equipement => this.equipementListFiltered.push(equipement));
                }
            });
            this.filter.equipementList = this.equipementListFiltered;
        },
        refreshEquipementBySousGroupe() {
            this.equipementListFiltered = [];
            this.filter.sousGroupeList.forEach(sousGroupe => {
                const equipements = this.equipementList.filter(equipement => equipement.sousGroupeId === sousGroupe.id);
                if (equipements != null) {
                    equipements.forEach(equipement => this.equipementListFiltered.push(equipement));
                }
            });
            this.filter.equipementList = this.equipementListFiltered;
        },
        exportXls() {
            const equipementIds = this.equipementList?.length === this.filter.equipementList.length ? [] : this.filter.equipementList.map(e => e.id);
            if (equipementIds.length > 500) {
                const response = {
                    status: 500
                };
                this.$store.commit(
                    `errorToaster/${ERROR.SET_MESSAGE}`,
                    ErrorManager.processErrorCustomMessage(response.status , this.$t('app.error.exportTooManyEquipments')),
                    { root: true }
                );
                return;
            }
            store.dispatch(`suiviInterventionPreventiveModuleState/${SuiviInterventionPreventiveActions.EXPORT_XLS_INTERVENTION_PREVENTIVE}`, {
                sort: this.sort && sortValue(this.sort.sortData, this.sort.sortStatus),
                etatIds: this.filterStatus?.length === this.statusList?.length ? [] : this.filterStatus.map(status => {
                    switch (status) {
                        case 'A_REALISER':
                            return 1;
                        case 'EN_COURS':
                            return 2;
                        case 'REALISE':
                            return 3;
                    }
                }),
                adresseSiteIds: this.filterSite?.length === this.siteListTotal?.length ? [] : this.filterSite.map(site => site.id),
                id: this.filterInterventionId !== '' ? this.filterInterventionId : undefined,
                groupeIds: this.filterGroupe?.length === this.groupeList.length ? [] : this.filterGroupe.map(groupe => groupe.id),
                sousGroupeIds: this.filterSousGroupe?.length === this.sousGroupeList.length ? [] : this.filterSousGroupe.map(ssGroupe => ssGroupe.id),
                equipementIds,
                dateDebut:
                    this.filterPredictedDateFrom !== '' ? this.filterPredictedDateFrom : undefined,
                dateFin:
                    this.filterPredictedDateTo !== '' ? this.filterPredictedDateTo : undefined,
            });
        },
    },
    computed: {
        tableData() {
            return store.getters['suiviInterventionPreventiveModuleState/getSuiviInterventionsItems'].map(
                ({ id, adresseSite, groupeLibelle, sousGroupeLibelle, equipement, typeIntervention, periodicite, dateDebutPrevue, dateFinReelle, etat, isRapportPresent }: InterventionPreventive) => {
                    etat = statusName(etat);
                    const periodiciteAsString = StringUtils.getPeriodiciteAsString(periodicite);
                    return {
                        id: { pageName: 'interventionPreventiveDetailView', value: id, label: id },
                        adresseSite,
                        groupeLibelle,
                        sousGroupeLibelle,
                        equipement,
                        typeIntervention,
                        periodiciteAsString,
                        dateDebutPrevue: dateDebutPrevue ? new Date(dateDebutPrevue).toLocaleDateString() : '',
                        dateFin: dateFinReelle ? new Date(dateFinReelle).toLocaleDateString() : '',
                        statut: etat,
                        pdfExist: isRapportPresent,
                    };
                }
            );
        },
        sort(): {
            sortData: string;
            sortStatus: SortType;
        } {
            return store.getters['suiviInterventionPreventiveModuleState/getSuiviInterventionsSort'];
        },
        tableHeader(): TableHeader[] {
            return store.getters['suiviInterventionPreventiveModuleState/getSuiviInterventionsTableHeader'];
        },
        page() {
            return store.getters['suiviInterventionPreventiveModuleState/getSuiviInterventionsPage'];
        },
        numberOfPages() {
            return store.getters['suiviInterventionPreventiveModuleState/getSuiviInterventionsNbOfPages'];
        },
        scroll() {
            return store.getters['suiviInterventionPreventiveModuleState/getSuiviInterventionsScroll'];
        },
        isLoading() {
            return store.getters['suiviInterventionPreventiveModuleState/getSuiviInterventionsIsLoading'];
        },
        filterSite(): any[] {
            return this.$store.getters['suiviInterventionPreventiveModuleState/getSuiviInterventionsFilterSite'];
        },
        filterStatus(): Array<string> {
            return store.getters['suiviInterventionPreventiveModuleState/getSuiviInterventionFilterStatus'];
        },
        filterPredictedDateFrom() {
            return store.getters['suiviInterventionPreventiveModuleState/getSuiviInterventionFilterPredictedDateFrom'];
        },
        filterPredictedDateTo() {
            return store.getters['suiviInterventionPreventiveModuleState/getSuiviInterventionFilterPredictedDateTo'];
        },
        filterSiteIds(): any[] {
            return store.getters['suiviInterventionPreventiveModuleState/getSuiviInterventionFilterSiteIds'];
        },
        filterGroupe(): any[] {
            return this.$store.getters['suiviInterventionPreventiveModuleState/getSuiviInterventionFilterGroupeIds'];
        },
        filterSousGroupe(): any[] {
            return this.$store.getters['suiviInterventionPreventiveModuleState/getSuiviInterventionFilterSousGroupeIds'];
        },
        filterEquipement(): any[] {
            return this.$store.getters['suiviInterventionPreventiveModuleState/getSuiviInterventionFilterEquipementIds'];
        },
        filterInterventionId() {
            return store.getters['suiviInterventionPreventiveModuleState/getSuiviInterventionFilterInterventionId'];
        },
        count() {
            return store.getters['suiviInterventionPreventiveModuleState/getSuiviInterventionsCount'];
        },
        iconeXls() {
            return require('@/assets/img/xls.svg');
        },
        isExportXlsLoading(): boolean {
            return store.getters['suiviInterventionPreventiveModuleState/getSuiviInterventionsExportXlsIsLoading'];
        },
        isExportDisabled(): boolean {
            return this.tableData.length === 0;
        },
    },
});
</script>

<style lang="scss" scoped>
@import '@/theme/pages-recherche.scss';
.datatable-container {
    max-height: calc(100vh - 290px);
    overflow-y: auto;
}
:deep(.containerBtn) {
    height: 21px;
}
:deep(.icon) {
    height: 20px;
}
.btn {
    width: 100%;
    border-radius: 5px;
    cursor: pointer;
    height: 40px;
    margin-top: 18px;
}
.filter-container {
    display: flex;
    padding: 0 16px;
    background: white;
    border-radius: 6px;
    margin-bottom: 8px;
}
.filter-site-container{
    flex-direction: column;
    width: 15%;
}
.filter {
    width: 100%;
}
</style>
